import React from "react";
import { Progress, Typography } from "antd";
import "./PasswordStrength.scss";

const { Text } = Typography;

interface PasswordStrengthProps {
  password: string;
  showStrength?: boolean;
}

const PasswordStrength: React.FC<PasswordStrengthProps> = ({
  password,
  showStrength = true,
}) => {
  const calculateStrength = (password: string) => {
    if (!password) return { score: 0, level: "", color: "" };

    let score = 0;
    const checks = {
      length: password.length >= 8,
      lowercase: /[a-z]/.test(password),
      uppercase: /[A-Z]/.test(password),
      number: /\d/.test(password),
      special: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
    };

    // Calculate score based on criteria
    Object.values(checks).forEach((check) => {
      if (check) score += 20;
    });

    // Determine level and color
    if (score < 60) {
      return { score, level: "Weak", color: "#ff4d4f" };
    } else if (score < 100) {
      return { score, level: "Medium", color: "#faad14" };
    } else {
      return { score: 100, level: "Strong", color: "#52c41a" };
    }
  };

  const getValidationMessages = (password: string) => {
    const messages = [];

    if (password.length < 8) {
      messages.push("At least 8 characters");
    }

    if (!/[A-Z]/.test(password)) {
      messages.push("At least one uppercase letter");
    }

    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
      messages.push("At least one special character");
    }

    if (!/\d/.test(password)) {
      messages.push("At least one number");
    }

    return messages;
  };

  const { score, level, color } = calculateStrength(password);
  const validationMessages = getValidationMessages(password);
  const isValid = validationMessages.length === 0 && password.length >= 8;

  if (!password) return null;

  return (
    <div className="password-strength-container">
      {showStrength && (
        <div className="strength-indicator">
          <div className="strength-header">
            <Text className="strength-label">Password strength:</Text>
            <Text className="strength-level" style={{ color }}>
              {level}
            </Text>
          </div>
          <Progress
            percent={score}
            showInfo={false}
            strokeColor={color}
            className="strength-progress"
          />
        </div>
      )}

      <div className="validation-messages">
        <Text className="requirements-label">Password requirements:</Text>
        <ul className="requirements-list">
          {validationMessages.map((message, index) => (
            <li key={index} className="requirement-item invalid">
              <Text type="danger">✗ {message}</Text>
            </li>
          ))}
          {isValid && (
            <li className="requirement-item valid">
              <Text type="success">✓ All requirements met</Text>
            </li>
          )}
        </ul>
      </div>
    </div>
  );
};

export default PasswordStrength;
